import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { UserMembership } from '../types/data-models';

interface TenantContextValue {
  /** Currently active tenant id (from the JWT). */
  activeTenantId: number | null;
  activeTenantName: string | null;
  /** The caller's role in the active workspace: owner/admin/member/guest. */
  activeTenantRole: string | null;
  /**
   * True when the active workspace is one shared with the caller rather than
   * one they belong to. Drives the "shared" marking and hides workspace-level
   * actions. Cosmetic — the backend enforces the same boundary independently.
   */
  isGuest: boolean;
  memberships: UserMembership[];
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, memberships } = useAuth();

  const value = useMemo<TenantContextValue>(() => {
    const activeTenantId = user?.tenantId ?? null;
    const active = memberships.find((m) => m.tenantId === activeTenantId);

    // `memberships` excludes guest rows by design, so an active tenant that
    // isn't in the list is one the user was invited into rather than joined.
    const isGuest = activeTenantId !== null && !active;

    return {
      activeTenantId,
      activeTenantName: active?.tenantName ?? null,
      activeTenantRole: active?.role ?? (isGuest ? 'guest' : null),
      isGuest,
      memberships,
    };
  }, [user, memberships]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
